import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { quotationsApi } from '../../services/api';
import { toast } from 'sonner';
import { FileText, Download, Loader2, Plus, Trash2 } from 'lucide-react';

const QuotationsPage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    school_name: '',
    contact_mobile: '',
    contact_email: '',
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await quotationsApi.list();
      setList(r.data.quotations || []);
    } catch (e) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.school_name || !form.contact_mobile) {
      toast.error('School name and mobile are required');
      return;
    }
    setCreating(true);
    try {
      const r = await quotationsApi.create(form);
      toast.success(`Quotation ${r.data.quote_number} created`);
      setForm({ school_name: '', contact_mobile: '', contact_email: '', notes: '' });
      load();
      window.open(quotationsApi.pdfUrl(r.data.id), '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quote_number}?`)) return;
    try {
      await quotationsApi.delete(q.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
          <FileText className="h-7 w-7 text-primary" /> Quotation Generator
        </h1>
        <p className="text-muted-foreground mt-1">
          One-click quote with your logo + contact at the top. Items are fixed:
          ₹15,000 one-time + ₹3,999/month billed annually as ₹47,988/year.
        </p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" /> New quotation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>School name *</Label>
              <Input
                value={form.school_name}
                onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                placeholder="e.g., GREENWOOD HIGH SCHOOL"
                required
                data-testid="quote-school-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact mobile *</Label>
              <Input
                value={form.contact_mobile}
                onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })}
                placeholder="+91-XXXXXXXXXX"
                required
                data-testid="quote-mobile-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Contact email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="principal@school.com"
                data-testid="quote-email-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any custom note that should appear on the quote"
                rows={2}
                data-testid="quote-notes-input"
              />
            </div>

            <div className="md:col-span-2 bg-muted/30 rounded p-3 text-sm">
              <p className="font-semibold mb-1">What's included (auto-filled in the PDF)</p>
              <ul className="text-muted-foreground list-disc list-inside space-y-0.5">
                <li>Basic development &amp; training charges (one-time) — ₹15,000</li>
                <li>Software subscription — ₹3,999/month, billed annually as ₹47,988</li>
                <li><b>Total: ₹62,988</b> (GST applied later if applicable)</li>
              </ul>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={creating} data-testid="create-quote-button">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Create &amp; download
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>All quotations ({list.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No quotations yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No.</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="font-mono">{q.quote_number}</TableCell>
                    <TableCell className="font-medium">{q.school_name}</TableCell>
                    <TableCell className="text-xs">
                      <div>{q.contact_mobile}</div>
                      <div className="text-muted-foreground">{q.contact_email || ''}</div>
                    </TableCell>
                    <TableCell className="font-semibold">
                      ₹{(q.total || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {(q.created_at || '').slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <a
                        href={quotationsApi.pdfUrl(q.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`quote-pdf-${q.id}`}
                      >
                        <Button size="sm" variant="outline" className="mr-2">
                          <Download className="h-4 w-4" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(q)}
                        data-testid={`quote-delete-${q.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuotationsPage;
